/**
 * Emissions Tracker Main Component
 *
 * This file is lazily loaded to reduce initial bundle size.
 * Entry point is in EmissionsTrackerRouteEntry.tsx
 */

import '@g17eco/ui-modern/styles/tailwind.css';
import './styles.scss';

import { useCallback, useEffect } from 'react';
import { Redirect, Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { RouteErrorBoundary } from '@features/error';
import { OrganizationSwitcherRoute } from '@routes/OrganizationSwitcherRoute';
import { AppRouteInterface } from '../../types/routes';
import { ProductCodes } from '../../types/products';
import { getInitiativeTree } from '../../selectors/initiativeTree';
import { useAppSelector } from '../../reducers';
import { SettingStorage } from '../../services/SettingStorage';
import { RootInitiativeData } from '../../types/initiative';
import config from '../../config';
import { emissionsTrackerRoutes, rootAppPath } from './routes';
import { emissionsTrackerRouteMatcher } from './routeMatcher';
import { EmissionsDashboardPage } from './pages/EmissionsDashboardPage';
import { EmissionsActivitiesPage } from './pages/EmissionsActivitiesPage';
import { EmissionsActivityFormPage } from './pages/EmissionsActivityFormPage';
import { EmissionsActivityDetailPage } from './pages/EmissionsActivityDetailPage';
import { EmissionsReportsPage } from './pages/EmissionsReportsPage';
import { EmissionsScope1Page } from './pages/EmissionsScope1Page';
import { EmissionsScope2Page } from './pages/EmissionsScope2Page';
import { EmissionsScope3Page } from './pages/EmissionsScope3Page';
import { EmissionsInsightsPage } from './pages/EmissionsInsightsPage';
import { EmissionsAdminPage } from './pages/EmissionsAdminPage';
import { EmissionFactorsPage } from './pages/EmissionFactorsPage';
import { EmissionsSettingsPage } from './pages/EmissionsSettingsPage';
import { EmissionsBulkImportPage } from './pages/EmissionsBulkImportPage';
import { EmissionsDataExportPage } from './pages/EmissionsDataExportPage';
import { EmissionsUserManagementRoute } from './pages/EmissionsUserManagementRoute';
import { EmissionsBusinessMetricsPage } from './pages/EmissionsBusinessMetricsPage';
import { EmissionsImportHistoryPage } from './pages/EmissionsImportHistoryPage';
import { EmissionsAssetsListPage } from './pages/EmissionsAssetsListPage';
import { EmissionsAssetDashboardPage } from './pages/EmissionsAssetDashboardPage';
import { EmissionsEvidenceLibraryPage } from './pages/EmissionsEvidenceLibraryPage';
import EmissionsTrackerLogo from '../../images/apps/Emissions_Tracker_logo.svg';
import { InitiativeStructureContainer } from '../../routes/initiative-structure/InitiativeStructureContainer';
import { WithEmissionsFilters } from './hooks/useEmissionsFilters';

const emissionsTrackerAppRoute: AppRouteInterface = {
  appName: 'Emissions Tracker',
  appIcon: EmissionsTrackerLogo,
  productCode: ProductCodes.EmissionTracker,
  routes: [
    emissionsTrackerRoutes.EMISSIONS_OVERVIEW,
    emissionsTrackerRoutes.EMISSIONS_ACTIVITIES,
    emissionsTrackerRoutes.EMISSIONS_ASSETS,
    emissionsTrackerRoutes.EMISSIONS_SCOPES,
    emissionsTrackerRoutes.EMISSIONS_INSIGHTS,
    emissionsTrackerRoutes.EMISSIONS_REPORTS,
    emissionsTrackerRoutes.EMISSIONS_ADMIN,
  ],
  hasReportingLevels: true,
};

const pathMatch = `/${rootAppPath}/:anyPage?/:initiativeId?/:subPage?`;

export const EmissionsTrackerRoute = () => {
  const history = useHistory();
  const match = useRouteMatch<{ initiativeId: string }>({ path: pathMatch });
  const initiativeId = match?.params?.initiativeId;

  const initiativeTree = useAppSelector(getInitiativeTree);
  const rootInitiatives = useAppSelector((state) => state.rootInitiatives.data);

  // Get available organizations for emissions tracker
  const organizations = emissionsTrackerRouteMatcher.filterInitiatives<RootInitiativeData>(rootInitiatives);
  const selectedId =
    organizations.length === 1 ? organizations[0]?._id : SettingStorage.getItem(emissionsTrackerRouteMatcher.storageKey);
  const organization = organizations.find((o) => o._id === selectedId) ?? organizations[0];
  const firstInitiativeId = organization?.firstInitiativeId;

  // Redirect to overview with first initiative when at base path
  // This handles the case where OrganizationSwitcherRoute can't redirect (no appConfig)
  useEffect(() => {
    if (!initiativeId && firstInitiativeId) {
      history.replace(`/${rootAppPath}/overview/${firstInitiativeId}`);
    }
  }, [initiativeId, firstInitiativeId, history]);

  const redirectHandler = useCallback(
    (newInitiativeId: string) => {
      history.push(`/${rootAppPath}/overview/${newInitiativeId}`);
    },
    [history]
  );

  const defaultParams: AppRouteInterface['defaultParams'] = {
    initiativeId,
  };

  // Feature flag - redirect to carbon calculator if not enabled
  if (!config.features.emissionsTracker) {
    return <Redirect to='/emissions-calculator' />;
  }

  return (
    <RouteErrorBoundary>
      <OrganizationSwitcherRoute
        appRoute={{
          ...emissionsTrackerAppRoute,
          defaultParams,
        }}
        routeMatcher={emissionsTrackerRouteMatcher}
        changeReportingLevelRoute={emissionsTrackerRoutes.EMISSIONS_OVERVIEW}
        redirectHandler={redirectHandler}
        initiativeTree={initiativeTree}
        pathMatch={pathMatch}
        mapRoute={emissionsTrackerRoutes.EMISSIONS_NAVIGATE_BY_MAP}
        rootAppPath={rootAppPath}
      >
        <Switch>
          {/* Initiative Structure Routes */}
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_NAVIGATE_BY_ARCHIVED.path}
            component={InitiativeStructureContainer}
          />
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_NAVIGATE_BY_MAP.path}
            component={InitiativeStructureContainer}
          />
          {/* Admin Routes */}
          <Route path={emissionsTrackerRoutes.EMISSIONS_SETTINGS.path} exact component={EmissionsSettingsPage} />
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_BULK_IMPORT.path}
            exact
            component={EmissionsBulkImportPage}
          />
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_DATA_EXPORT.path}
            exact
            component={EmissionsDataExportPage}
          />
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_MANAGE_USERS.path}
            component={EmissionsUserManagementRoute}
          />
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_FACTORS.path}
            exact
            component={EmissionFactorsPage}
          />
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_BUSINESS_METRICS.path}
            exact
            component={EmissionsBusinessMetricsPage}
          />
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_IMPORT_HISTORY.path}
            exact
            component={EmissionsImportHistoryPage}
          />
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_EVIDENCE_LIBRARY.path}
            exact
            component={EmissionsEvidenceLibraryPage}
          />
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_ADMIN.path}
            exact
            component={EmissionsAdminPage}
          />
          {/* Activity Form Routes */}
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_ACTIVITY_NEW.path}
            exact
            component={EmissionsActivityFormPage}
          />
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_ACTIVITY_EDIT.path}
            exact
            component={EmissionsActivityFormPage}
          />
          {/* Activity Detail Page — must come after NEW/EDIT so the bare
              :activityId pattern doesn't swallow `/new` or `/:id/edit` */}
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_ACTIVITY_DETAIL.path}
            exact
            render={() => (
              <WithEmissionsFilters>
                <EmissionsActivityDetailPage />
              </WithEmissionsFilters>
            )}
          />
          {/* Main Navigation: Activities */}
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_ACTIVITIES.path}
            exact
            render={() => (
              <WithEmissionsFilters>
                <EmissionsActivitiesPage />
              </WithEmissionsFilters>
            )}
          />
          {/* Main Navigation: Reports */}
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_REPORTS.path}
            component={EmissionsReportsPage}
          />
          {/* Main Navigation: Emissions - Scope Sub-tabs */}
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_SCOPE1.path}
            exact
            render={() => (
              <WithEmissionsFilters>
                <EmissionsScope1Page />
              </WithEmissionsFilters>
            )}
          />
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_SCOPE2.path}
            exact
            render={() => (
              <WithEmissionsFilters>
                <EmissionsScope2Page />
              </WithEmissionsFilters>
            )}
          />
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_SCOPE3.path}
            exact
            component={EmissionsScope3Page}
          />
          {/* Main Navigation: Emissions (defaults to Scope 1) */}
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_SCOPES.path}
            exact
            render={() => (
              <WithEmissionsFilters>
                <EmissionsScope1Page />
              </WithEmissionsFilters>
            )}
          />
          {/* Main Navigation: Insights (Analytics) */}
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_INSIGHTS.path}
            exact
            render={() => (
              <WithEmissionsFilters>
                <EmissionsInsightsPage />
              </WithEmissionsFilters>
            )}
          />
          {/* Main Navigation: Assets — dashboard route (with :assetId) declared
              first so the more specific path wins over the list path */}
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_ASSET_DASHBOARD.path}
            exact
            render={() => (
              <WithEmissionsFilters>
                <EmissionsAssetDashboardPage />
              </WithEmissionsFilters>
            )}
          />
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_ASSETS.path}
            exact
            render={() => (
              <WithEmissionsFilters>
                <EmissionsAssetsListPage />
              </WithEmissionsFilters>
            )}
          />
          {/* Main Navigation: Overview */}
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_OVERVIEW.path}
            exact
            render={() => (
              <WithEmissionsFilters>
                <EmissionsDashboardPage />
              </WithEmissionsFilters>
            )}
          />
          {/* Legacy Dashboard Route - redirect to Overview */}
          <Route
            path={emissionsTrackerRoutes.EMISSIONS_DASHBOARD.path}
            exact
            render={() => (
              <WithEmissionsFilters>
                <EmissionsDashboardPage />
              </WithEmissionsFilters>
            )}
          />
          {/* Default Route */}
          <Route
            path={pathMatch}
            render={() => (
              <WithEmissionsFilters>
                <EmissionsDashboardPage />
              </WithEmissionsFilters>
            )}
          />
        </Switch>
      </OrganizationSwitcherRoute>
    </RouteErrorBoundary>
  );
};
